import React, { useState, useEffect } from "react";
import { Input, InputGroup } from "reactstrap";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { LoginInput, LoginButton } from "../styled";
import { initRegistrationService } from "../../../services/authServices";

export const Step1EmailVerification = ({
  email,
  setEmail,
  otp,
  setOtp,
  emailVerified,
  stepErrors,
  setStepErrors,
  onVerify,
  onResendOtp,
  isLoading,
  userStore,
  userType,
  form,
}) => {
  const [emailInitiated, setEmailInitiated] = useState(false);
  const [initiatingEmail, setInitiatingEmail] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const isCompany = userType === "COMPANY";

  useEffect(() => {
    let timer;
    if (emailInitiated && resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [emailInitiated, resendCountdown]);

  const handleInitializeEmail = async () => {
    if (!email) {
      setStepErrors({ email: "Email is required" });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStepErrors({ email: "Invalid email format" });
      return;
    }

    // Validate company name if COMPANY userType
    if (isCompany) {
      const companyNameError = form?.validateCompanyName?.();
      if (companyNameError) {
        setStepErrors({ companyName: companyNameError });
        return;
      }
    }

    setInitiatingEmail(true);
    setStepErrors({});

    try {
      const payload = {
        email,
        userType: userType || "INDIVIDUAL_USER",
        ...(userType === "COMPANY" && form?.companyName
          ? { companyName: form.companyName }
          : {}),
      };

      const response = await initRegistrationService(payload);
      if (response.status !== 200) {
        const errorData = await response.data;
        console.error("Email initialization failed:", errorData, response);
        throw new Error(
          errorData.message || "Failed to initialize email registration"
        );
      }
      toast.success(
        `OTP sent to ${email}. Valid for 10 minutes.`,
        {
          duration: 5000,
        }
      );
      setEmailInitiated(true);
      setResendCountdown(60); // 1 minute in seconds
      setInitiatingEmail(false);
      setStepErrors({});
    } catch (error) {
      setStepErrors({ email: error.message || "Failed to send OTP" });
    } finally {
      setInitiatingEmail(false);
    }
  };

  return (
    <LoginInput>
      {isCompany && (
        <div className="mb-4">
          <label>Company Name</label>
          <div className="input-group">
            <Input
              type="text"
              className="form-control"
              placeholder="Enter your company name"
              value={form.companyName || ""}
              onChange={(e) => {
                form.setCompanyName(e.target.value);
                setStepErrors({ ...stepErrors, companyName: "" });
              }}
              disabled={emailInitiated || emailVerified}
            />
          </div>
          {stepErrors.companyName && <h3>{stepErrors.companyName}</h3>}
        </div>
      )}



      <div className="mb-4">
        <label>{isCompany ? "Company Email" : "Email Address"}</label>
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "flex-start",
          }}
        >
          <div className="input-group" style={{ flex: 1 }}>
            <Input
              type="email"
              className="form-control"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setStepErrors({ ...stepErrors, email: "" });
              }}
              disabled={emailInitiated || emailVerified}
            />
          </div>
          {emailInitiated && !emailVerified && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setEmailInitiated(false);
                setOtp("");
                setResendCountdown(0);
                setStepErrors({});
              }}
              style={{
                fontSize: "12px",
                padding: "6px 12px",
                whiteSpace: "nowrap",
              }}
            >
              ✎ Change
            </button>
          )}
        </div>
        {stepErrors.email && <h3>{stepErrors.email}</h3>}

        {isCompany && (
          <div
            style={{
              marginTop: "8px",
            }}
          >
            <p
              style={{
                margin: "0",
                fontSize: "12px",
                color: "#666",
                lineHeight: "1.4",
              }}
            >
              This email address will be the primary contact to notify your
              company about all activities in your account.
            </p>
          </div>
        )}
      </div>

      {!emailInitiated && (
        <LoginButton>
          <div className="text-center">
            <button
              type="button"
              className="btn btn-primary px-5 mb-2"
              onClick={handleInitializeEmail}
              disabled={initiatingEmail}
              style={{
                width: "100%",
              }}
            >
              {initiatingEmail ? "Sending..." : "Register"}
            </button>
          </div>
        </LoginButton>
      )}

      {emailInitiated && !emailVerified && (
        <>
          <div className="mb-4">
            <label>OTP (One Time Password)</label>
            <div className="input-group">
              <InputGroup>
                <Input
                  type="text"
                  className="form-control"
                  placeholder="Enter 6-digit OTP"
                  maxLength="6"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setOtp(value);
                    setStepErrors({ ...stepErrors, otp: "" });
                  }}
                />
              </InputGroup>
            </div>
            {stepErrors.otp && <h3>{stepErrors.otp}</h3>}
            <div style={{ marginTop: "10px", fontSize: "14px" }}>
              Didn't get the OTP?{" "}
              {resendCountdown > 0 ? (
                <span style={{ color: "#999" }}>
                  Resend in {resendCountdown}s
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    handleInitializeEmail();
                    setResendCountdown(40);
                  }}
                  style={{
                    color: "#0066cc",
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    padding: "0",
                    fontSize: "14px",
                    textDecoration: "underline",
                  }}
                >
                  Resend
                </button>
              )}
            </div>
          </div>

          <LoginButton>
            <div className="text-center mt-5">
              <button
                type="button"
                className="btn btn-primary px-5 mb-2"
                onClick={onVerify}
                disabled={emailVerified || isLoading}
              >
                {isLoading ? "Verifying..." : "Verify Email"}
              </button>
            </div>
          </LoginButton>
        </>
      )}

      <div
        style={{
          backgroundColor: "#fafafa",
          padding: "16px",
          marginBottom: "20px",
          borderRadius: "4px",
          fontSize: "13px",
          textAlign: "center",
          lineHeight: "1.6",
          color: "#555",
        }}
      >
        <p style={{ margin: "0 0 8px 0" }}>
          By registering, you accept our{" "}
          <a
            href="https://rosabon-finance.com/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0066cc",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Terms of Use
          </a>{" "}
          and{" "}
          <a
            href="https://rosabon-finance.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0066cc",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Privacy Policy
          </a>
        </p>
      </div>

      <p className="text-center">
        Already have an account?{" "}
        <span className="">
          <Link to="/login">Sign in </Link>
        </span>
      </p>
    </LoginInput>
  );
};
